import { useEffect, useState } from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Download, Calendar, Clock, MapPin, Users } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";


interface TrainingSession {
  id: string;
  title: string;
  description: string;
  instructor: string;
  location: string;
  start_date: string;
  end_date: string;
  status: string;
}

interface Participant {
  user_id: string;
  full_name: string;
  email: string;
  was_present: boolean;
}

const SessionReport = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useSupabaseUser();
  const { isAdmin, isLoading: isLoadingRoles } = useUserRoles();
  const [session, setSession] = useState<TrainingSession | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoadingRoles && user && isAdmin && sessionId) {
      fetchReportData();
    }
  }, [user, isAdmin, isLoadingRoles, sessionId]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      // Fetch session details
      const { data: sessionData, error: sessionError } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;
      setSession(sessionData);

      // Fetch session participants
      const { data: participantsData, error: participantsError } = await supabase
        .from('training_session_participants')
        .select('user_id')
        .eq('session_id', sessionId);

      if (participantsError) throw participantsError;

      // Fetch profiles for the participants
      const userIds = (participantsData as any)?.map((p: any) => p.user_id) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Fetch attendance records
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('training_attendance' as any)
        .select('user_id, was_present')
        .eq('session_id', sessionId);

      if (attendanceError && attendanceError.code !== 'PGRST116') {
        throw attendanceError;
      }

      // Create attendance map
      const attendanceMap = new Map();
      (attendanceData as any)?.forEach((record: any) => {
        attendanceMap.set(record.user_id, record.was_present);
      });

      // Combine participant data with attendance status and profiles
      const participantsWithAttendance = userIds.map((userId: string) => {
        const profile = (profilesData as any)?.find((p: any) => p.id === userId);
        return {
          user_id: userId,
          full_name: profile?.full_name || 'Unknown',
          email: profile?.email || '',
          was_present: attendanceMap.get(userId) || false
        };
      });

      setParticipants(participantsWithAttendance);

    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error(language === 'en' ? 'Failed to load report data' : 'Αποτυχία φόρτωσης δεδομένων αναφοράς');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    const element = document.getElementById('report-content');
    const opt = {
      margin: 0.5,
      filename: `training-session-report-${sessionId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };

    const { default: html2pdf } = await import("html2pdf.js");
    html2pdf().set(opt).from(element).save();
    toast.success(language === 'en' ? 'Report downloaded successfully' : 'Η αναφορά μεταφορτώθηκε με επιτυχία');
  };

  if (loading || isLoadingRoles) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {language === 'en' ? 'Access Denied' : 'Απαγόρευση Πρόσβασης'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'en' 
              ? 'You do not have permission to access this page.' 
              : 'Δεν έχετε άδεια πρόσβασης σε αυτή τη σελίδα.'
            }
          </p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {language === 'en' ? 'Session Not Found' : 'Η Συνεδρία Δεν Βρέθηκε'}
          </h1>
          <Button onClick={() => navigate('/training/sessions')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Back to Sessions' : 'Πίσω στις Συνεδρίες'}
          </Button>
        </div>
      </div>
    );
  }

  const presentCount = participants.filter(p => p.was_present).length;
  const absentCount = participants.length - presentCount;
  const attendanceRate = participants.length > 0 ? (presentCount / participants.length * 100).toFixed(1) : '0';

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {language === 'en' ? 'Training Session Report' : 'Αναφορά Εκπαιδευτικής Συνεδρίας'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'en' ? 'Attendance report for training session' : 'Αναφορά παρουσίας για εκπαιδευτική συνεδρία'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/training/sessions/${sessionId}/participants`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Back to Participants' : 'Πίσω στους Συμμετέχοντες'}
          </Button>
          <Button onClick={generatePDF}>
            <Download className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Download PDF' : 'Λήψη PDF'}
          </Button>
        </div>
      </div>

      <div id="report-content" className="space-y-4 bg-white p-4 rounded-lg" style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
        {/* Header */}
        <div className="text-center border-b border-gray-300 pb-3">
          <h1 className="text-lg font-bold mb-2 text-black">
            {language === 'en' ? 'TRAINING SESSION ATTENDANCE REPORT' : 'ΑΝΑΦΟΡΑ ΠΑΡΟΥΣΙΑΣ ΕΚΠΑΙΔΕΥΤΙΚΗΣ ΣΥΝΕΔΡΙΑΣ'}
          </h1>
          <p className="text-xs text-gray-600">
            {language === 'en' ? `Generated on ${format(new Date(), 'MMMM dd, yyyy HH:mm')}` : `Δημιουργήθηκε στις ${format(new Date(), 'dd MMMM yyyy HH:mm')}`}
          </p>
        </div>

        {/* Session Details */}
        <div className="border border-gray-300 rounded p-3">
          <div className="flex items-center mb-2">
            <Calendar className="h-4 w-4 mr-1 text-gray-700" />
            <h2 className="text-sm font-semibold text-black">
              {language === 'en' ? 'Session Information' : 'Πληροφορίες Συνεδρίας'}
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <h3 className="font-semibold text-black mb-1">{language === 'en' ? 'Title' : 'Τίτλος'}:</h3>
              <p className="text-gray-700">{session.title}</p>
            </div>
            <div>
              <h3 className="font-semibold text-black mb-1">{language === 'en' ? 'Description' : 'Περιγραφή'}:</h3>
              <p className="text-gray-700">{session.description || 'N/A'}</p>
            </div>
            <div>
              <h3 className="font-semibold text-black mb-1">{language === 'en' ? 'Date & Time' : 'Ημερομηνία & Ώρα'}:</h3>
              <p className="text-gray-700">{format(fromLocalISOString(session.start_date), 'MMM dd, yyyy HH:mm')}</p>
            </div>
            <div>
              <h3 className="font-semibold text-black mb-1">{language === 'en' ? 'Instructor' : 'Εκπαιδευτής'}:</h3>
              <p className="text-gray-700">{session.instructor || 'N/A'}</p>
            </div>
            <div>
              <h3 className="font-semibold text-black mb-1">{language === 'en' ? 'Location' : 'Τοποθεσία'}:</h3>
              <p className="text-gray-700">{session.location || 'N/A'}</p>
            </div>
            <div>
              <h3 className="font-semibold text-black mb-1">{language === 'en' ? 'Status' : 'Κατάσταση'}:</h3>
              <p className="text-gray-700 capitalize">{session.status}</p>
            </div>
          </div>
        </div>

        {/* Attendance Summary */}
        <div className="grid grid-cols-4 gap-3">
          <div className="border border-gray-300 rounded p-3 bg-white text-center">
            <div className="text-xl font-bold text-green-600 mb-1">{presentCount}</div>
            <p className="text-xs text-gray-600">
              {language === 'en' ? 'Present' : 'Παρόντες'}
            </p>
          </div>
          <div className="border border-gray-300 rounded p-3 bg-white text-center">
            <div className="text-xl font-bold text-red-600 mb-1">{absentCount}</div>
            <p className="text-xs text-gray-600">
              {language === 'en' ? 'Absent' : 'Απόντες'}
            </p>
          </div>
          <div className="border border-gray-300 rounded p-3 bg-white text-center">
            <div className="text-xl font-bold text-gray-800 mb-1">{participants.length}</div>
            <p className="text-xs text-gray-600">
              {language === 'en' ? 'Total Participants' : 'Σύνολο Συμμετεχόντων'}
            </p>
          </div>
          <div className="border border-gray-300 rounded p-3 bg-white text-center">
            <div className="text-xl font-bold text-blue-600 mb-1">{attendanceRate}%</div>
            <p className="text-xs text-gray-600">
              {language === 'en' ? 'Attendance Rate' : 'Ποσοστό Παρουσίας'}
            </p>
          </div>
        </div>

        {/* Detailed Attendance List */}
        <div className="border border-gray-300 rounded overflow-hidden">
          <div className="flex items-center p-3 bg-gray-50 border-b border-gray-300">
            <Users className="h-4 w-4 mr-1 text-gray-700" />
            <h2 className="text-sm font-semibold text-black">
              {language === 'en' ? 'Detailed Attendance List' : 'Αναλυτική Λίστα Παρουσίας'}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-300">
                  <th className="px-3 py-2 text-left font-semibold text-black">
                    {language === 'en' ? 'Name' : 'Όνομα'}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-black">
                    {language === 'en' ? 'Email' : 'Email'}
                  </th>
                  <th className="px-3 py-2 text-center font-semibold text-black">
                    {language === 'en' ? 'Attendance' : 'Παρουσία'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {participants.map((participant, index) => (
                  <tr key={participant.user_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-2 text-black border-b border-gray-200">{participant.full_name}</td>
                    <td className="px-3 py-2 text-gray-700 border-b border-gray-200">{participant.email}</td>
                    <td className="px-3 py-2 text-center border-b border-gray-200">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        participant.was_present 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`} style={{
                        backgroundColor: participant.was_present ? '#dcfce7' : '#fee2e2',
                        color: participant.was_present ? '#166534' : '#991b1b'
                      }}>
                        {participant.was_present 
                          ? (language === 'en' ? 'Present' : 'Παρών')
                          : (language === 'en' ? 'Absent' : 'Απών')
                        }
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionReport;
